import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import type { PageBlockPatchInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const I18n = z.record(z.string());
const BlocksMap = z.record(I18n);
export type BlocksMap = z.infer<typeof BlocksMap>;

const BlocksResponse = z.object({ blocks: BlocksMap });

/** All overrides for a page, as { "<block_key>": {it,en,de}, ... }. */
export function usePageBlocks(page: string) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["page-content", page],
    enabled: !!token && !!page,
    queryFn: async (): Promise<BlocksMap> => {
      const r = await apiFetch("/api/v1/admin/page-content", token!, BlocksResponse, { query: { page } });
      return r.blocks;
    },
  });
}

const SaveResponse = z.object({
  page: z.string(),
  block_key: z.string(),
  value: I18n,
  updated_at: z.string(),
});

/** Upsert one block override. */
export function useSavePageBlock() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: (body: PageBlockPatchInput) =>
      apiFetch("/api/v1/admin/page-content", token!, SaveResponse, { method: "PATCH", body }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["page-content", vars.page] });
    },
  });
}
